/**
 * 建产品抽屉（添加物料改版，Frank 2026-10-30；设计「添加物料：搜索、申购与新建产品方案」三～五节）。
 *
 * 人传实物照片和网页截图（可贴链接）→ 后台「分析产品图片」读出名称、多语名、描述、规格、分类、包装、价格 →
 * 全部摊开、每项可改 → 查重（Jev）：产品目录里已有就「用这个」→ 建。不再走 intake-preview。
 * - 每传一张图 / 贴了链接就重新分析（连着传几张，等最后一张传完）；人改过的格子不被覆盖。右上角「重新分析」。
 * - 实物照片长边 720、截图长边 1568（读价格和小字）。
 * - 建产品 409 同名：同样给「用这个」。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ExternalLink, Loader2, Plus, RefreshCw, X } from 'lucide-react'
import { CreateFlyout } from '@/components/ui/create-flyout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { productsApi } from '@/api'
import { linkAttachmentToEntity } from '@/services/upload-service'
import { LinksEditor } from '@/components/shared/links/LinksEditor'
import { PRODUCT_PHOTO_COMPRESSION, SCREENSHOT_COMPRESSION } from '@/utils/imageCompressor'
import { rememberProductAlias } from '@/features/asset-mgr/catalogGuard'
import {
  analyzeBody, applyAnalysis, buildCreatePayload, emptyForm, hardGate, packProblem, sameNameProductId, setPack, setPackPrice, touch,
  I18N_LANGS, MAX_ANALYZE_IMAGES, MAX_NAME_CHARS, MAX_PACK_NAME_CHARS, MAX_TEXT_CHARS,
  type AnalyzeResult, type DuplicateProduct, type DuplicateResult, type Field, type ProductForm,
} from '@/features/asset-mgr/product-analyze/analyzeForm'
import { usePermission } from '@/hooks/usePermission'
import type { ProductItemType, ProductTrackingMode } from '@/types/asset-mgr'
import type { SnowflakeId } from '@/types/snowflake'
import { asSnowflakeId } from '@/types/snowflake'
import { cn } from '@/lib/utils'
import { CategoryTreeSelect } from '../_shared'
import { AssetPhotoEditor } from '../asset/AssetPhotoEditor'

const ITEM_TYPES: ProductItemType[] = ['CONSUMABLE', 'SPARE_PART', 'EQUIPMENT', 'TOOL']
const TRACKING: ProductTrackingMode[] = ['QUANTITY', 'SERIALIZED']
const ITEM_TYPE_KEY: Record<ProductItemType, string> = { EQUIPMENT: 'equipment', SPARE_PART: 'sparePart', CONSUMABLE: 'consumable', TOOL: 'tool' }
const TRACKING_KEY: Record<ProductTrackingMode, string> = { SERIALIZED: 'serialized', QUANTITY: 'quantity' }
/** 连着传几张图：最后一次改动后等这么久再分析 */
const AUTO_ANALYZE_MS = 800

export interface ProductCreateDrawerProps {
  open: boolean
  onClose: () => void
  /** 预填「这是什么」（例如搜索框里没搜到的那句），有它就打开即分析 */
  initialText?: string
  /** 建好、或选了已有产品：调用方选中它 */
  onCreated: (product: { id: SnowflakeId; name: string }, how: 'created' | 'existing') => void
  /** 批量收编时第几个 / 共几个 */
  progress?: { index: number; total: number }
}

const errMsg = (e: any, fallback: string) => e?.response?.data?.detail || e?.response?.data?.msg || e?.message || fallback

/** 只看图和链接：它们变了才自动重新分析（改字要点「重新分析」） */
const autoKey = (f: ProductForm) => {
  const b = analyzeBody(f)
  return JSON.stringify([b.images, b.links])
}

export function ProductCreateDrawer({ open, onClose, initialText = '', onCreated, progress }: ProductCreateDrawerProps) {
  const { t } = useTranslation('productCatalog')
  const [form, setForm] = useState<ProductForm>(() => emptyForm(initialText))
  const formRef = useRef(form)
  formRef.current = form
  const [analyzing, setAnalyzing] = useState(false)
  const [analysis, setAnalysis] = useState<'idle' | 'done' | 'unavailable' | 'failed'>('idle')
  const [dup, setDup] = useState<DuplicateResult | null>(null)
  const [dupChecking, setDupChecking] = useState(false)
  const [sameName, setSameName] = useState<{ productId: string; name: string } | null>(null)
  // 设计「分析结果全部摊开」：各语言默认展开，可收起
  const [showLangs, setShowLangs] = useState(true)
  // 两个上传框各自在传的数（传图期间不让建）
  const [uploading, setUploading] = useState(0)
  const { hasPermission } = usePermission()
  const canUpdateProduct = hasPermission('asset-mgr:product:update')
  const [saving, setSaving] = useState(false)
  const attachmentIds = useRef<Map<string, string>>(new Map())
  const analyzeSeq = useRef(0)
  const dupSeq = useRef(0)
  /** 上次查重用的名字和分类：变了才再查 */
  const dupAsked = useRef<string | null>(null)
  const analyzedKey = useRef<string | null>(null)
  const textRef = useRef<HTMLTextAreaElement>(null)

  // ── 查重 ──────────────────────────────────────────────────────────
  const checkDuplicate = useCallback(async (f: ProductForm) => {
    const name = f.name.trim()
    const seq = ++dupSeq.current
    dupAsked.current = JSON.stringify([name, f.categoryId, f.notThese])
    if (!name) { setDup(null); setDupChecking(false); return }
    setDupChecking(true)
    try {
      const res = await productsApi.duplicateCheck({ name, text: f.text.trim() || name, categoryId: f.categoryId, notThese: f.notThese }) as unknown as DuplicateResult
      if (seq === dupSeq.current) setDup(res)
    } catch {
      if (seq === dupSeq.current) setDup(null)
    } finally {
      if (seq === dupSeq.current) setDupChecking(false)
    }
  }, [])

  // ── 分析 ──────────────────────────────────────────────────────────
  const analyze = useCallback(async (f: ProductForm) => {
    const body = analyzeBody(f)
    if (!body.text && !body.images.length && !body.links.length) return
    const seq = ++analyzeSeq.current
    analyzedKey.current = autoKey(f)
    setAnalyzing(true)
    try {
      const res = await productsApi.analyze(body) as unknown as AnalyzeResult
      if (seq !== analyzeSeq.current) return
      const next = applyAnalysis(formRef.current, res)
      setForm(next)
      setAnalysis(res.unavailable ? 'unavailable' : 'done')
      void checkDuplicate(next)
    } catch {
      if (seq !== analyzeSeq.current) return
      setAnalysis('failed')
      // 分析失败也要查重（人手填名字建）
      void checkDuplicate(formRef.current)
    } finally {
      if (seq === analyzeSeq.current) setAnalyzing(false)
    }
  }, [checkDuplicate])

  // 每次打开重置；带了「这是什么」就直接分析
  useEffect(() => {
    if (!open) return
    const f = emptyForm(initialText)
    setForm(f)
    setAnalysis('idle')
    setDup(null)
    setSameName(null)
    setShowLangs(true)
    setUploading(0)
    dupAsked.current = null
    attachmentIds.current.clear()
    analyzeSeq.current++
    dupSeq.current++
    setAnalyzing(false)
    setDupChecking(false)
    analyzedKey.current = autoKey(f)
    if (initialText.trim()) void analyze(f)
    // 抽屉关着时就挂载了，autoFocus 不起作用：打开时手动聚焦
    setTimeout(() => textRef.current?.focus(), 50)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 只在打开时重置
  }, [open])

  // 图、链接变了：等最后一次改动后自动重新分析
  const currentKey = autoKey(form)
  useEffect(() => {
    if (!open || currentKey === analyzedKey.current) return
    const timer = setTimeout(() => void analyze(formRef.current), AUTO_ANALYZE_MS)
    return () => clearTimeout(timer)
  }, [open, currentKey, analyze])

  const edit = (field: Field, patch: Partial<ProductForm>) => setForm(f => touch(f, field, patch))
  const patch = (p: Partial<ProductForm>) => setForm(f => ({ ...f, ...p }))

  // ── 用已有产品 ────────────────────────────────────────────────────
  const pickExisting = async (p: { productId: string; name: string }, addPack = false) => {
    const f = formRef.current
    setSaving(true)
    try {
      if (addPack && f.pack) {
        // 同数已有时后端回已有那条（名字可能不同）：用回包的名字
        const opt = await productsApi.addPurchaseOption(p.productId, { units: f.pack.units, name: f.pack.name.trim() || null }) as unknown as { name?: string }
        toast.success(t('create.toastPackAdded', { pack: opt?.name || f.pack.name || t('pack.namePlaceholderFor', { count: f.pack.units }), name: p.name }))
      } else {
        toast.message(t('create.toastUsingExisting', { name: p.name }))
      }
    } catch (e) {
      toast.error(errMsg(e, t('create.toastFailed')))
      setSaving(false)
      return
    }
    setSaving(false)
    // 记住人的叫法（别名）；不挡流程
    const said = f.text.trim()
    if (said) void rememberProductAlias(asSnowflakeId(p.productId), said, p.name)
    onCreated({ id: asSnowflakeId(p.productId), name: p.name }, 'existing')
    onClose()
  }
  /** 「用这个，加 N pack」：有包装、个数合法、能改那个产品（product:update）才给 */
  const canAddPack = !!form.pack && !packProblem(form.pack) && canUpdateProduct
  const packLabel = form.pack ? (form.pack.name.trim() || t('pack.namePlaceholderFor', { count: form.pack.units })) : ''

  const notThese = (ids: string[]) => {
    const next = { ...formRef.current, notThese: [...new Set([...formRef.current.notThese, ...ids])] }
    setForm(next)
    void checkDuplicate(next)
  }

  // ── 建 ────────────────────────────────────────────────────────────
  const gate = hardGate(form)
  // 查重说「已有」：先选「用这个」或「不是这个」，免得建出重复产品
  const dupUnresolved = dup?.status === 'matched' && !!dup.product && !form.notThese.includes(dup.product.productId)
  // 图还在传、或传完还没分析（防抖中）、查重还没回来：都不让建
  const pendingAnalysis = autoKey(form) !== analyzedKey.current
  const busy = analyzing || dupChecking || uploading > 0 || pendingAnalysis
  const create = async () => {
    if (gate || saving || busy || dupUnresolved) return
    setSaving(true)
    setSameName(null)
    try {
      const f = formRef.current
      const saved = await productsApi.create(buildCreatePayload(f)) as unknown as { id: SnowflakeId; name: string; pendingReview?: boolean }
      // 照片列已随 body 写入；这里把上传时建的附件行挂到新产品上（同 DetailsTab），失败不算失败
      const ids = [...f.photos, ...f.screenshots].map(u => attachmentIds.current.get(u)).filter((id): id is string => !!id)
      if (ids.length) {
        await Promise.all(ids.map(id => linkAttachmentToEntity(id, { entityType: 'product', entityId: String(saved.id), categoryCode: 'product_photo' }))).catch(() => {})
      }
      toast.success(!f.categoryId ? t('create.toastCreatedPending', { name: saved.name }) : t('create.toastCreated', { name: saved.name }))
      onCreated({ id: saved.id, name: saved.name }, 'created')
      onClose()
    } catch (e) {
      const existing = sameNameProductId(e)
      if (existing) {
        // 同名比对忽略大小写和空格：给调用方目录里的真名
        const real = await (productsApi.get(existing) as Promise<unknown>).then(r => (r as { name?: string })?.name).catch(() => undefined)
        setSameName({ productId: existing, name: real || formRef.current.name.trim() })
      } else toast.error(errMsg(e, t('create.toastFailed')))
    } finally {
      setSaving(false)
    }
  }

  const footerInfo = gate
    ? <span className="text-xs text-destructive">{t(`create.blocked.${gate}`, { max: MAX_NAME_CHARS })}</span>
    : dupUnresolved
      ? <span className="text-xs text-amber-700 dark:text-amber-400">{t('create.dup.decide')}</span>
      : !form.categoryId && analysis !== 'idle'
        ? <span className="text-xs text-amber-700 dark:text-amber-400">{t('create.noCategoryPending')}</span>
        : null

  const label = (text: string, htmlFor?: string) => (
    <label className="mb-1 block text-xs font-medium text-muted-foreground" htmlFor={htmlFor}>{text}</label>
  )

  return (
    <CreateFlyout
      open={open}
      onClose={onClose}
      title={progress && progress.total > 1 ? t('create.titleProgress', { index: progress.index, total: progress.total }) : t('create.title')}
      footerInfo={footerInfo}
      secondaryAction={{ label: t('common:action.cancel'), onClick: onClose, disabled: saving }}
      primaryAction={{ label: t('create.btnCreate'), onClick: () => void create(), loading: saving, disabled: !!gate || busy || dupUnresolved, perm: 'asset-mgr:product:create' }}
    >
      <div className="space-y-4 px-6 py-4" data-testid="product-create-drawer">
        <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">{t('create.intro')}</p>

        <div>
          {label(t('create.textLabel'), 'product-create-text')}
          <Textarea
            id="product-create-text"
            ref={textRef}
            rows={2}
            value={form.text}
            onChange={e => patch({ text: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void analyze(formRef.current) }}
            maxLength={MAX_TEXT_CHARS}
            placeholder={t('create.textPlaceholder')}
            className="text-sm"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            {label(t('create.photos'))}
            <AssetPhotoEditor
              photos={form.photos}
              droppable
              thumbSize={64}
              compress={PRODUCT_PHOTO_COMPRESSION}
              onBusyChange={b => setUploading(n => Math.max(0, n + (b ? 1 : -1)))}
              onChange={async (next) => { patch({ photos: next }) }}
              onUploadedAttachment={(url, id) => attachmentIds.current.set(url, id)}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">{t('create.photosHint')}</p>
          </div>
          <div>
            {label(t('create.screenshots'))}
            <AssetPhotoEditor
              photos={form.screenshots}
              droppable
              thumbSize={64}
              compress={SCREENSHOT_COMPRESSION}
              onBusyChange={b => setUploading(n => Math.max(0, n + (b ? 1 : -1)))}
              onChange={async (next) => { patch({ screenshots: next }) }}
              onUploadedAttachment={(url, id) => attachmentIds.current.set(url, id)}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">{t('create.screenshotsHint')}</p>
          </div>
        </div>

        <div>
          {label(t('links.title'))}
          <LinksEditor value={form.links} onChange={links => patch({ links })} />
        </div>
        {form.photos.length + form.screenshots.length > MAX_ANALYZE_IMAGES && (
          <p className="text-[11px] text-amber-700 dark:text-amber-400">{t('create.tooManyImages', { max: MAX_ANALYZE_IMAGES })}</p>
        )}

        <div className="rounded-lg border border-border" data-testid="product-details">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <span className="text-sm font-medium text-foreground">{t('create.details')}</span>
            {analyzing && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="analyzing">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />{t('create.analyzing')}
              </span>
            )}
            <Button type="button" variant="outline" size="sm" className="ml-auto h-7 text-xs" disabled={analyzing}
              onClick={() => void analyze(formRef.current)}>
              <RefreshCw className="mr-1 h-3 w-3" />{analysis === 'idle' ? t('create.analyze') : t('create.reanalyze')}
            </Button>
          </div>

          <div className="space-y-3 px-3 py-3">
            {(analysis === 'unavailable' || analysis === 'failed') && (
              <div className="text-xs text-amber-700 dark:text-amber-400">{t(analysis === 'failed' ? 'create.analyzeFailed' : 'create.analyzeUnavailable')}</div>
            )}

            {renderDuplicate()}

            {sameName && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/40 dark:bg-amber-950/40" data-testid="same-name">
                <div className="text-sm text-foreground">{t('create.sameName', { name: sameName.name })}</div>
                <Button type="button" size="sm" className="mt-1.5 h-7 px-2 text-xs" disabled={saving} onClick={() => void pickExisting(sameName)}>{t('create.dup.useThis')}</Button>
              </div>
            )}

            <div>
              {label(t('create.nameEn'), 'product-create-name')}
              <Input
                id="product-create-name"
                value={form.name}
                onChange={e => edit('name', { name: e.target.value })}
                onBlur={() => {
                  const f = formRef.current
                  if (JSON.stringify([f.name.trim(), f.categoryId, f.notThese]) !== dupAsked.current) void checkDuplicate(f)
                }}
                placeholder={t('create.namePlaceholder')}
                maxLength={MAX_NAME_CHARS + 50}
                className="h-8 text-sm"
                data-testid="product-name-input"
              />
            </div>

            <div>
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground hover:underline" onClick={() => setShowLangs(v => !v)}>
                {showLangs ? t('create.hideLanguages') : t('create.showLanguages')}
              </button>
              {showLangs && (
                <div className="mt-2 space-y-2" data-testid="product-languages">
                  {I18N_LANGS.map(lang => (
                    <div key={lang} className="grid grid-cols-[4.5rem_1fr] items-start gap-2">
                      <span className="pt-1.5 text-xs text-muted-foreground">{t(`create.lang.${lang}`)}</span>
                      <div className="space-y-1">
                        <Input value={form.nameI18n[lang] ?? ''} aria-label={t('create.nameIn', { lang: t(`create.lang.${lang}`) })}
                          onChange={e => edit(`nameI18n.${lang}`, { nameI18n: { ...formRef.current.nameI18n, [lang]: e.target.value } })}
                          className="h-8 text-sm" />
                        <Textarea rows={2} value={form.descriptionI18n[lang] ?? ''} aria-label={t('create.descriptionIn', { lang: t(`create.lang.${lang}`) })}
                          onChange={e => edit(`descriptionI18n.${lang}`, { descriptionI18n: { ...formRef.current.descriptionI18n, [lang]: e.target.value } })}
                          className="text-xs" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              {label(t('create.rows.category'))}
              <CategoryTreeSelect
                value={form.categoryId}
                onChange={(cid) => {
                  const next = touch(formRef.current, 'categoryId', { categoryId: cid ? String(cid) : null })
                  setForm(next)
                  void checkDuplicate(next)
                }}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Select value={form.itemType} onValueChange={v => patch({ itemType: v as ProductItemType })}>
                <SelectTrigger className="h-8 w-40 text-xs" aria-label={t('create.itemType')}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ITEM_TYPES.map(it => <SelectItem key={it} value={it}>{t(`assets:productDetailsTab.itemTypeOptions.${ITEM_TYPE_KEY[it]}`)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={form.trackingMode} onValueChange={v => patch({ trackingMode: v as ProductTrackingMode })}>
                <SelectTrigger className="h-8 w-40 text-xs" aria-label={t('create.trackingMode')}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRACKING.map(m => <SelectItem key={m} value={m}>{t(`assets:productDetailsTab.trackingOptions.${TRACKING_KEY[m]}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              {label(t('create.rows.spec'))}
              <div className="space-y-1.5" data-testid="spec-rows">
                {form.specification.map((s, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Input value={s.key} placeholder={t('create.specKey')} aria-label={t('create.specKey')} className="h-8 w-32 text-sm"
                      onChange={e => edit('specification', { specification: formRef.current.specification.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)) })} />
                    <Input value={s.value} placeholder={t('create.specValue')} aria-label={t('create.specValue')} className="h-8 flex-1 text-sm"
                      onChange={e => edit('specification', { specification: formRef.current.specification.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })} />
                    <button type="button" className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                      title={t('create.removeSpec')} aria-label={t('create.removeSpec')}
                      onClick={() => edit('specification', { specification: formRef.current.specification.filter((_, j) => j !== i) })}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs"
                  onClick={() => edit('specification', { specification: [...formRef.current.specification, { key: '', value: '' }] })}>
                  <Plus className="mr-1 h-3 w-3" />{t('create.addSpec')}
                </Button>
              </div>
            </div>

            <div>
              {label(t('create.rows.pack'))}
              {renderPack()}
            </div>

            <div className="flex flex-wrap items-end gap-3">
              {form.pack && (
                <div>
                  {label(t('create.pricePack'), 'product-create-pack-price')}
                  <Input id="product-create-pack-price" type="number" min={0} step="0.01" value={form.packPrice ?? ''} placeholder="0.00" className="h-8 w-28 text-sm"
                    onChange={e => setForm(f => setPackPrice(f, e.target.value === '' ? null : Number(e.target.value)))} />
                </div>
              )}
              {form.currency && form.currency !== 'USD' && (
                <span className="pb-2 text-[11px] text-amber-700 dark:text-amber-400">{t('create.currencyNote', { currency: form.currency })}</span>
              )}
              <div>
                {label(t('create.priceEach'), 'product-create-price')}
                <Input id="product-create-price" type="number" min={0} step="0.01" value={form.price ?? ''} placeholder="0.00" className="h-8 w-28 text-sm"
                  onChange={e => edit('price', { price: e.target.value === '' ? null : Number(e.target.value) })} />
              </div>
            </div>

            <div>
              {label(t('create.description'), 'product-create-description')}
              <Textarea id="product-create-description" rows={2} value={form.description} className="text-sm"
                onChange={e => edit('description', { description: e.target.value })} />
            </div>
          </div>
        </div>
      </div>
    </CreateFlyout>
  )

  function renderDuplicate() {
    if (dupChecking && !dup) {
      return <div className="flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />{t('create.dup.checking')}</div>
    }
    if (!dup || dup.status === 'none') return null
    const hidden = new Set(form.notThese)
    if (dup.status === 'matched' && dup.product && !hidden.has(dup.product.productId)) {
      const p = dup.product
      return (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/40 dark:bg-amber-950/40" data-testid="dup-matched">
          <div className="flex items-center gap-1.5 text-sm text-foreground">
            <span>{t('create.dup.matched', { name: p.name })}</span>
            <a href={`/asset-mgr/products?productId=${p.productId}`} target="_blank" rel="noopener noreferrer" aria-label={p.name}>
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
            </a>
          </div>
          {p.category && <div className="text-[11px] text-muted-foreground">{p.category}</div>}
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Button type="button" size="sm" className="h-7 px-2 text-xs" disabled={saving} onClick={() => void pickExisting(p)}>{t('create.dup.useThis')}</Button>
            {canAddPack && (
              <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={saving} onClick={() => void pickExisting(p, true)}>
                {t('create.dup.useThisAddPack', { pack: packLabel })}
              </Button>
            )}
            <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => notThese([p.productId])}>{t('create.dup.notThis')}</Button>
          </div>
        </div>
      )
    }
    const cands = (dup.status === 'ask' ? dup.candidates : []).filter(c => !hidden.has(c.productId))
    if (!cands.length) return null
    return (
      <div className="space-y-1.5 rounded-md border border-border px-3 py-2" data-testid="dup-ask">
        <div className="text-xs text-foreground">{t('create.dup.ask')}</div>
        {cands.map((c: DuplicateProduct) => (
          <div key={c.productId} className="flex flex-wrap items-center gap-1.5 text-sm">
            <a href={`/asset-mgr/products?productId=${c.productId}`} target="_blank" rel="noopener noreferrer" className="min-w-0 truncate text-foreground hover:underline">{c.name}</a>
            {c.category && <span className="text-[11px] text-muted-foreground">{c.category}</span>}
            <Button type="button" size="sm" variant="outline" className="ml-auto h-6 px-2 text-xs" disabled={saving} onClick={() => void pickExisting(c)}>{t('create.dup.useThis')}</Button>
            {canAddPack && (
              <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={saving} onClick={() => void pickExisting(c, true)}>
                {t('create.dup.useThisAddPack', { pack: packLabel })}
              </Button>
            )}
          </div>
        ))}
        <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => notThese(cands.map(c => c.productId))}>{t('create.dup.noneOfThese')}</Button>
      </div>
    )
  }

  function renderPack() {
    const pack = form.pack
    if (!pack) {
      return (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>{t('create.pack.none')}</span>
          <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => setForm(f => setPack(f, { units: 0, name: '' }))}>
            {t('create.pack.add')}
          </Button>
        </div>
      )
    }
    const bad = packProblem(pack)
    return (
      <div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="pack-row">
          <Input
            type="number" min={2} step={1} inputMode="numeric"
            value={pack.units || ''}
            onChange={e => setForm(f => setPack(f, { ...pack, units: e.target.value === '' ? 0 : Number(e.target.value) }))}
            placeholder={t('pack.unitsPlaceholder')}
            className={cn('h-8 w-20 text-sm', bad && 'border-destructive')}
            aria-label={t('pack.units')}
          />
          <Input
            value={pack.name}
            onChange={e => setForm(f => setPack(f, { ...pack, name: e.target.value }))}
            maxLength={MAX_PACK_NAME_CHARS}
            placeholder={pack.units >= 2 ? t('pack.namePlaceholderFor', { count: pack.units }) : t('pack.namePlaceholder')}
            className="h-8 w-36 text-sm"
            aria-label={t('pack.name')}
          />
          <button type="button" className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
            title={t('create.pack.remove')} aria-label={t('create.pack.remove')} onClick={() => setForm(f => setPack(f, null))}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        {bad && <div className="mt-1 text-[11px] text-destructive">{t('purchaseOptions.unitsMin')}</div>}
        {!bad && <div className="mt-1 text-[11px] text-muted-foreground">{t('create.pack.hint', { count: pack.units })}</div>}
      </div>
    )
  }
}
